import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, User, Tag, ArrowRight } from 'lucide-react';
import { blogPosts } from '@/data/blogPosts';
import { AIAssistantWidget } from '@/components/AIAssistantWidget';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Blog = () => {
  const sortedPosts = blogPosts.sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <title>Life Insurance Blog - Expert Tips & Guides | Safurra</title>
      <meta 
        name="description" 
        content="Expert life insurance advice, tips, and guides. Learn how to choose the right coverage, compare policies, and protect your family's financial future." 
      />
      
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary-light/30 to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Life Insurance Blog
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Expert advice, tips, and guides to help you make informed decisions 
              about life insurance and protect your family's financial future.
            </p>
            <Link to="/calculator">
              <Button size="lg" variant="hero">
                Calculate Your Coverage
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Latest Articles
              </h2>
              <p className="text-xl text-muted-foreground">
                Stay informed with our expert insights on life insurance and financial planning.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {sortedPosts.map((post, index) => (
                <Card key={post.slug} className="hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
                  {post.featuredImage && (
                    <div className="w-full aspect-video rounded-t-lg overflow-hidden">
                      <img 
                        src={post.featuredImage} 
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader className="flex-shrink-0">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(post.publishDate)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {post.readTime}
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {post.author}
                      </div>
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground hover:text-primary transition-colors">
                      <Link to={`/blog/${post.slug}`}>
                        {post.title}
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow flex flex-col justify-between">
                    <p className="text-muted-foreground mb-4 flex-grow">
                      {post.excerpt}
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        {post.tags.slice(0, 2).map((tag, tagIndex) => (
                          <span 
                            key={tagIndex}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                          >
                            <Tag className="h-3 w-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <Link to={`/blog/${post.slug}`} className="block">
                        <Button variant="outline" size="sm" className="w-full">
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Stay Updated
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get the latest life insurance tips and financial planning advice delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border border-border bg-background"
              />
              <Button size="lg">Subscribe</Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* AI Assistant Widget */}
      <AIAssistantWidget pageContext="blog" />
    </div>
  );
};

export default Blog;